import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { IconButton, Typography } from "@material-ui/core";
import { DebouncedSearchInput } from "@/components/SearchInput";
import { IconGroups, IconSwitch } from "@/components/Icon";
import Collapser from "@/components/Collapser";
import { useTranslation } from "@/langs/useTranslation";
import LoadingIndicator from "@/components/commons/LoadingIndicator";
import {
  flattenTreeData,
  mergeTreeData,
  mergeTreeDataByGroup
} from "@/helpers/utils";
import { getCurrentCompanyId } from "@/apis/getCurrentCompanyId";
import { getGroupTreeListApi } from "@/apis/groupsApi";
import {
  createGroupApi,
  deleteGroupApi,
  moveGroupAPI,
  updateGroupApi,
  getDevicesByGroupIdAndSearch
} from "@/apis/apiCollection";
import CategoryTree from "./CategoryTree";
import styles from "./index.module.less";

const CategoryContent = ({ collapse, children }) => {
  const { t } = useTranslation();

  if (collapse) {
    return (
      <Collapser
        style={{ marginTop: 16 }}
        title={
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            <IconGroups size={16} color="primary" style={{ marginRight: 5 }} />
            {t({ defaultMessage: "门店" })}
          </Typography>
        }
      >
        {children}
      </Collapser>
    );
  }
  return children;
};

const Category = React.forwardRef(
  (
    {
      showSwitch = true,
      showAll = true,
      collapse = true,
      checkStrictly,
      selectable = true,
      editable = true,
      sortable = true,
      importable = true,
      groupCheckable = true,
      height,
      withPermission,
      readOnly,
      userMode,
      deviceMode,
      description,
      searchPlaceholder,
      defaultSelectAll,
      disabledCheckAllNode,
      disableRoot,
      checkable: checkableFromProps,
      selectedKeys: selectedKeysFromProps,
      checkedKeys: checkedKeysFromProps,
      loadedKeys: loadedKeysFromProps,
      defaultSelectedKeys,
      defaultCheckedKeys,
      defaultExpandedKeys,
      defaultLoadedKeys,
      onCheck,
      onSelect,
      onSwitch,
      onChangeRename,
      onInputRename,
      onNodeImport,
      onLoaded,
      onLoadFailed
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [treeData, setTreeData] = useState([]);
    const [checkable, setCheckable] = useState(false);

    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState();
    const [selectedKeys, setSelectedKeys] = useState();
    const [checkedKeys, setCheckedKeys] = useState();
    const [loadedKeys, setLoadedKeys] = useState();
    const selectedKeysFromPropsRef = useRef(null);
    selectedKeysFromPropsRef.current = selectedKeysFromProps;
    const groupTreeDataRef = useRef(null);

    const treeTopNodeNameMap = {
      all: userMode
        ? t({ defaultMessage: "所有账号" })
        : t({ defaultMessage: "所有设备" }),
      default: t({ defaultMessage: "仓储" })
    };

    const formatTree = tree => {
      return tree.map((item, i) => {
        const id = item.id;
        const name = item.name;
        const isRoot = name === "all" || id === getCurrentCompanyId();
        const nodeName = isRoot ? treeTopNodeNameMap[name] || name : name;
        return {
          key: id,
          id,
          title: nodeName,
          name: nodeName,
          showAction: !(isRoot && name === "all"),
          isRoot,
          disabled: disableRoot && isRoot ? true : false,
          checkable: groupCheckable
            ? disabledCheckAllNode && name === "all"
              ? false
              : undefined
            : false,
          count: userMode
            ? item.user_count || 0
            : !deviceMode
            ? item.devices_count || 0
            : undefined,
          subCount: userMode
            ? undefined // item.invisible_user_count
            : item.children && item.children.length > 0
            ? item.devices_count_including_children
            : undefined,
          companyId: item.company_id,
          parentId: item.parent_id,
          orderNO: item.order_no || i,
          selectable: (withPermission && item.hasPermission) ?? undefined,
          isLeaf: deviceMode ? false : !item.children || !item.children.length,
          children:
            item.children && item.children.length
              ? formatTree(item.children)
              : []
        };
      });
    };

    const getTreeGroup = async noReset => {
      try {
        if (!noReset) {
          setLoading(true);
        }
        const data = await getGroupTreeListApi();
        if (data && data.groups && data.groups.length > 0) {
          const treeData = formatTree(
            data.groups.filter(group => (!showAll ? group.id !== "all" : true))
          );
          setTreeData(treeData);
          if (deviceMode) {
            groupTreeDataRef.current = treeData;
          }
          if (!noReset) {
            setExpandedKeys(treeData.map(item => item.key));
            if (
              deviceMode &&
              treeData.length > 0 &&
              treeData[0].children.length > 0
            ) {
              loadDevicesData(treeData[0]);
            }
            if (treeData.length > 1 && !selectedKeysFromPropsRef.current) {
              setSelectedKeys([treeData[0].key]);
            }
            if (defaultSelectAll) {
              setCheckedKeys(treeData.map(item => item.key));
            }
            onLoaded?.(treeData);
          }
        } else {
          setTreeData([]);
          if (deviceMode) {
            groupTreeDataRef.current = [];
          }
          setExpandedKeys([]);
          setSelectedKeys([]);
          setCheckedKeys([]);
          onLoaded?.([]);
        }
      } catch (e) {
        onLoadFailed?.(e);
      } finally {
        if (!noReset) {
          setLoading(false);
        }
      }
    };

    const getDevices = async (groupId, keywords) => {
      const { devices } = await getDevicesByGroupIdAndSearch({
        groupId,
        keywords,
        additionalType: 1 // 0 默认 1 CMS
      });
      return devices;
    };

    const loadDevicesData = async ({ key, children }) => {
      // if (children && children.length) {
      //   return Promise.resolve();
      // }
      const devices = await getDevices(key, "");

      setLoadedKeys(prev => [...(prev || []), key]);

      if (!devices.length) return Promise.resolve();

      const devicesData = devices.map(item => ({
        parentId: key,
        key: item.id,
        name: item.serialNumber,
        checkable: true,
        isLeaf: true,
        ...item,
        title: item.deviceName
          ? `${item.serialNumber}(${item.deviceName})`
          : item.serialNumber
      }));

      setTreeData(prevData => mergeTreeData(prevData, key, devicesData));
    };

    const asyncGetChildrenKeys = async groupId => {
      return (await getDevices(groupId, "")).map(item => item.id);
    };

    const handleNodeMove = async result => {
      await moveGroupAPI({
        groups: result.list.map((item, i) => ({
          id: item.id,
          title: item.title,
          parent_id: item.parentId,
          order_no: i
        }))
      });
    };

    const handleNodeRename = async (node, name) => {
      onChangeRename?.(node, name);
      await updateGroupApi({
        id: node.id,
        name
      });
    };

    const handleNodeAdd = async (node, name) => {
      // // 当前公司id作为仓储(default)节点id
      // const defaultNode =
      //   node.parentId === node.companyId ||
      //   node.parentId === "default" ||
      //   node.parentId === getCurrentCompanyId();
      return createGroupApi({
        name,
        // parent_id: defaultNode ? "" : node.parentId,
        parent_id: node.parentId,
        order_no: node.orderNO
      });
    };

    const handleNodeDelete = async node => {
      await deleteGroupApi({
        id: node.id
      });
      if (!checkableFromProps) {
        const key = treeData[0].key;
        setSelectedKeys([key]);
        onSelect?.(key, {
          event: "select",
          node: treeData[0],
          selected: true,
          selectedNodes: [treeData[0]]
        });
      }
    };

    const handleSearch = async e => {
      const value = e.target.value;
      if (!deviceMode) {
        setSearchValue(value);
      } else {
        const resetData = () => {
          setTreeData(groupTreeDataRef.current);
          setExpandedKeys(groupTreeDataRef.current.map(item => item.key));
          setLoadedKeys([]);
          setSearchValue("");
        };
        if (!value) {
          resetData();
          return;
        }
        const devices = await getDevices(undefined, value);
        if (devices.length > 0) {
          const devicesData = devices.map(item => ({
            parentId: item.groupId,
            id: item.id,
            key: item.id,
            name: item.serialNumber,
            checkable: true,
            isLeaf: true,
            ...item,
            title: item.deviceName
              ? `${item.serialNumber}(${item.deviceName})`
              : item.serialNumber
          }));
          const { result, expandKeys } = mergeTreeDataByGroup(
            groupTreeDataRef.current,
            devicesData
          );
          setTreeData(result);
          setLoadedKeys(expandKeys);
          if (expandKeys.length > 0) {
            setExpandedKeys(expandKeys);
          }
          setSearchValue(value);
        } else {
          resetData();
        }
      }
    };

    useImperativeHandle(ref, () => ({
      reload: getTreeGroup,
      getTreeData: () => treeData,
      getFlattenTreeData: () => {
        return flattenTreeData(treeData);
      },
      setSelectedKeys
    }));

    useEffect(() => {
      getTreeGroup();
    }, []);

    useEffect(() => {
      if (checkableFromProps !== undefined) {
        setCheckable(checkableFromProps);
      }
    }, [checkableFromProps]);

    useEffect(() => {
      if (checkedKeysFromProps !== undefined) {
        setCheckedKeys(checkedKeysFromProps);
      }
    }, [checkedKeysFromProps]);

    useEffect(() => {
      if (loadedKeysFromProps !== undefined) {
        setLoadedKeys(loadedKeysFromProps);
      }
    }, [loadedKeysFromProps]);

    useEffect(() => {
      if (selectedKeysFromProps !== undefined) {
        setSelectedKeys(selectedKeysFromProps);
      }
    }, [selectedKeysFromProps]);

    return loading ? (
      <div className={styles.loadingGroup}>
        <LoadingIndicator />
      </div>
    ) : (
      <div>
        {showSwitch && (
          <div className={styles.switch}>
            <span>
              {checkable
                ? t({ defaultMessage: "切换单选" })
                : t({ defaultMessage: "切换多选" })}
            </span>
            <IconButton
              onClick={() => {
                setCheckable(prev => !prev);
                setCheckedKeys([]);
                onSwitch?.(!checkable);
              }}
            >
              <IconSwitch />
            </IconButton>
          </div>
        )}
        <DebouncedSearchInput
          placeholder={searchPlaceholder || t({ defaultMessage: "搜索门店" })}
          iconPosition="left"
          onDebounceChange={handleSearch}
        />
        {description}
        <CategoryContent collapse={collapse}>
          <CategoryTree
            checkStrictly={checkStrictly}
            editable={editable}
            sortable={sortable}
            importable={importable}
            height={height}
            searchable={!deviceMode}
            deviceMode={deviceMode}
            searchValue={searchValue}
            setTreeData={setTreeData}
            treeData={treeData}
            selectable={selectable}
            checkable={checkable}
            showAll={showAll}
            readOnly={readOnly}
            expandedKeys={expandedKeys}
            selectedKeys={selectedKeys}
            checkedKeys={checkedKeys}
            loadedKeys={loadedKeys}
            defaultSelectedKeys={defaultSelectedKeys}
            defaultCheckedKeys={defaultCheckedKeys}
            defaultExpandedKeys={defaultExpandedKeys}
            defaultLoadedKeys={defaultLoadedKeys}
            asyncGetChildrenKeys={asyncGetChildrenKeys}
            onCheck={onCheck}
            onSelect={onSelect}
            onNodeMove={handleNodeMove}
            onNodeRename={handleNodeRename}
            onInputRename={onInputRename}
            onNodeAdd={handleNodeAdd}
            onNodeDelete={handleNodeDelete}
            onNodeImport={onNodeImport}
            loadData={deviceMode ? loadDevicesData : undefined}
          />
        </CategoryContent>
      </div>
    );
  }
);

export default Category;
